import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State
} from '@stencil/core';

@Component({
  tag: 'uui-dialog',
  styleUrl: 'uui-dialog.css',
  shadow: true
})
export class UuiDialog implements ComponentInterface {
  hasTriggerSlot = false;

  /**
   * Should render trigger button/icon.
   */
  @Prop({ reflect: true }) renderTrigger: boolean = true;

  /**
   * Trigger button text.
   */
  @Prop({ reflect: true }) triggerText: string = 'Open Dialog';

  /**
   * Dismiss dialog on overlay click.
   */
  @Prop({ reflect: true }) dismissOnOverlay: boolean = true;

  /**
   * Dismiss dialog on Esc click.
   */
  @Prop({ reflect: true }) dismissOnEsc: boolean = true;

  /**
   * Dialog data.
   */
  @Prop({ reflect: true, mutable: true }) data: any = {};

  /**
   * Whether the dialog should be opened.
   */
  @Prop({ reflect: true, mutable: true }) opened = false;

  /**
   * Allow background scroll while dialog is active. Doesn't make any modifications to body element when set to true
   */
  @Prop({ reflect: true, mutable: true }) enableBackgroundScroll = false;

  /**
   * The underlying HTML element of this dialog.
   */
  @Element()
  el: HTMLElement;

  @State() dialog;

  componentWillLoad() {
    this.hasTriggerSlot = !!this.el.querySelector('[slot="trigger"]');
  }

  componentDidLoad(): void {
    // Override closing the dialog on Esc click
    if (this.dialog && !this.dismissOnEsc) {
      this.dialog.addEventListener('cancel', (e: Event) => {
        e.preventDefault();
      });
    }
    if (this.dialog && this.opened) {
      this.open();
    }
  }

  @Listen('click')
  handleClick(e: MouseEvent) {
    // Close the dialog when the user clicks on the overlay
    const target = e.composedPath()[0] as HTMLElement;
    if (this.dismissOnOverlay && target.tagName === 'DIALOG') {
      this.close({ cancelled: true });
    }
  }

  @Listen('keydown')
  handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      this.close({ cancelled: true });
    }
  }

  /**
   * Trigger dialog open emitting dialogOpened event.
   */
  @Method()
  async open() {
    const { data, dialog, dialogOpened } = this;
    this.opened = true;
    dialog.showModal();
    if (!this.enableBackgroundScroll) {
      this.preventBgScroll();
    }

    dialogOpened.emit(data);
  }

  /**
   * Trigger dialog close emitting dialogClosed event.
   */
  @Method()
  async close({ cancelled }: { cancelled?: boolean }) {
    const { data, dialog, dialogClosed } = this;
    this.opened = false;
    dialog.close();
    if (!this.enableBackgroundScroll) { 
      this.enableBgScroll();
    }
    
    dialogClosed.emit({ data, cancelled });
  }

  /**
   * Emitted on dialog open.
   */
  @Event()
  dialogOpened: EventEmitter;

  /**
   * Emitted on dialog close.
   */
  @Event()
  dialogClosed: EventEmitter;

  preventBgScroll() {
    document.body.style.position = 'fixed';
    document.body.style.overflowY = 'scroll';
    document.body.style.width = '100%';
  }

  enableBgScroll() {
    document.body.style.position = 'static';
    document.body.style.overflowY = 'auto';
    document.body.style.width = 'auto';
  }

  onCancel() {
    this.close({ cancelled: true });
  }

  onConfirm() {
    this.close({ cancelled: false });
  }

  render() {
    const { hasTriggerSlot, renderTrigger, triggerText } = this;

    const trigger = (
      <div>
        {hasTriggerSlot ? (
          <div onClick={this.open.bind(this)}>
            <slot name="trigger" />
          </div>
        ) : renderTrigger ? (
          <uui-button onClick={this.open.bind(this)}>{triggerText}</uui-button>
        ) : null}
      </div>
    );

    return (
      // see more on parts: https://stenciljs.com/docs/styling#css-parts
      <Host exportparts="header body footer">
        {trigger}

        <dialog ref={el => (this.dialog = el)}>
          <div part="header" class="dialog__header">
            <slot name="header" />
          </div>

          <div part="body" class="dialog__body">
            <slot name="body" />
          </div>

          <div part="footer" class="dialog__footer">
            <slot name="footer" />
          </div>
        </dialog>
      </Host>
    );
  }
}
